// src/utils/clientIdentity.js
//
// Stable client identity helpers
// ==============================
//
// PURPOSE
// -------
// This module provides stable client identifiers used by backend session binding:
//
//   - Device-Id
//   - Browser-Id
//
// These identifiers are intentionally stored in localStorage so they survive:
// - full page reloads
// - new tabs
// - browser restarts (as long as localStorage persists)
//
// CURRENT AUTH MODEL CONTEXT
// --------------------------
// Your backend session model binds auth sessions to this tuple:
//
//   (user_id, device_id, browser_id)
//
// Therefore Device-Id and Browser-Id must be:
//
// - stable
// - deterministic
// - shared across tabs of the same browser profile
// - never silently rotated during normal auth flows
//
// IMPORTANT DO NOTS
// -----------------
// 1) Do NOT casually rename the storage keys after deployment.
//    That would effectively create a "new client identity" from backend perspective
//    and would break session continuity.
//
// 2) Do NOT reset these values during:
//    - login
//    - logout
//    - refresh
//    - route changes
//
// 3) Do NOT treat these identifiers as security credentials.
//    They are only stable tuple selectors.
//    Real auth still depends on backend session validation and token/cookie checks.
//
// BROWSER-ID SPECIAL RULE
// -----------------------
// Browser-Id may be re-synchronized from backend via X-Browser-Id response header.
// That is why this module exposes:
//
//   setBrowserId(newBrowserId)
//
// DEVICE-ID SPECIAL RULE
// ----------------------
// Device-Id is frontend-owned and should remain stable.
// Backend should normally not replace it.
//
// SECURITY PRIORITY NOTE
// ----------------------
// In the new no-recovery auth policy, these identifiers still remain stable,
// but they do NOT imply that a new/duplicated/restored tab should auto-recover
// a user session.
//
// They are tuple identifiers only.
// Session selection remains a separate, explicit tab-local concern.

const DEV = process.env.NODE_ENV !== "production";

/* -------------------------------------------------------------------------- */
/* Storage keys                                                               */
/* -------------------------------------------------------------------------- */

/**
 * IMPORTANT:
 * These keys are intentionally kept stable for continuity with already stored data.
 *
 * Even though more namespaced keys such as "hb_device_id" / "hb_browser_id"
 * would be cleaner, changing them now could break session continuity for
 * existing browser profiles.
 */
const LS_DEVICE_ID = "deviceId";
const LS_BROWSER_ID = "browserId";

/* -------------------------------------------------------------------------- */
/* Validation                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Validate identity values stored in localStorage.
 *
 * We intentionally keep validation permissive:
 * - 8..128 chars
 * - letters / digits / underscore / dash
 *
 * Why:
 * - supports UUID-based values
 * - supports prefixed generated values like "dev_xxx" or "brw_xxx"
 * - rejects obviously broken / corrupted localStorage values
 */
function isValidIdentityValue(value) {
  const s = String(value || "").trim();
  return /^[a-zA-Z0-9_-]{8,128}$/.test(s);
}

/* -------------------------------------------------------------------------- */
/* Safe localStorage helpers                                                  */
/* -------------------------------------------------------------------------- */

/**
 * localStorage access may fail in:
 * - private browsing edge cases
 * - restricted environments
 * - disabled storage scenarios
 *
 * Therefore all access here is best-effort and exception-safe.
 */
function safeGetLS(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetLS(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore storage failures
  }
}

function safeRemoveLS(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore storage failures
  }
}

/* -------------------------------------------------------------------------- */
/* ID generation                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Generate a stable-looking client identity string.
 *
 * Preferred source:
 * - crypto.randomUUID()
 *
 * Fallback:
 * - still acceptable because this value is not a security token
 * - it is only a stable client identifier
 */
function generateIdentityId(prefix = "id") {
  try {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return `${prefix}_${crypto.randomUUID().replace(/[^a-zA-Z0-9_-]/g, "")}`;
    }
  } catch {
    // ignore and fall back
  }

  return `${prefix}_${Math.random().toString(16).slice(2)}_${Date.now().toString(16)}`;
}

/* -------------------------------------------------------------------------- */
/* Generic ensure/load helper                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Return a stable identity from localStorage or create a new one when needed.
 *
 * New value is generated when current localStorage entry is:
 * - missing
 * - invalid
 * - corrupted
 *
 * This helper intentionally does NOT distinguish "device" vs "browser" logic.
 * That distinction is expressed only by:
 * - storage key
 * - generation prefix
 */
function ensureIdentity(storageKey, prefixForGeneration) {
  const existing = safeGetLS(storageKey);

  if (isValidIdentityValue(existing)) {
    return String(existing).trim();
  }

  const next = generateIdentityId(prefixForGeneration);
  safeSetLS(storageKey, next);

  if (DEV) {
    // eslint-disable-next-line no-console
    console.log(`[clientIdentity] ${storageKey} created or repaired.`);
  }

  return next;
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Return a stable Device-Id for this browser profile.
 *
 * Semantics in this app:
 * - roughly identifies the browser profile / device context
 * - shared across all tabs
 * - should almost never change
 *
 * IMPORTANT:
 * - do not reset during normal auth flows
 * - do not let backend silently replace it
 */
export function getDeviceId() {
  return ensureIdentity(LS_DEVICE_ID, "dev");
}

/**
 * Return a stable Browser-Id for this browser profile.
 *
 * Semantics in this app:
 * - shared across all tabs
 * - may be canonicalized by backend
 * - must stay aligned with backend cookie/session binding expectations
 */
export function getBrowserId() {
  return ensureIdentity(LS_BROWSER_ID, "brw");
}

/**
 * Update Browser-Id from backend canonical value.
 *
 * Typical flow:
 * - backend returns X-Browser-Id response header
 * - apiClient reads that header
 * - apiClient calls setBrowserId(...)
 *
 * IMPORTANT:
 * - invalid values are ignored
 * - identical values are ignored silently
 * - this function should only be used for backend canonicalization,
 *   not for ad-hoc frontend identity rotation
 */
export function setBrowserId(newBrowserId) {
  const next = String(newBrowserId || "").trim();
  if (!isValidIdentityValue(next)) {
    if (DEV) {
      // eslint-disable-next-line no-console
      console.warn("[clientIdentity] setBrowserId ignored invalid value.");
    }
    return;
  }

  const current = safeGetLS(LS_BROWSER_ID);
  if (String(current || "").trim() === next) {
    return;
  }

  safeSetLS(LS_BROWSER_ID, next);

  if (DEV) {
    // eslint-disable-next-line no-console
    console.log("[clientIdentity] browserId updated from backend canonical value.");
  }
}

/**
 * Small diagnostic helper.
 *
 * Useful in:
 * - debug panels
 * - console diagnostics
 * - temporary troubleshooting instrumentation
 */
export function getClientIdentitySnapshot() {
  return {
    deviceId: getDeviceId(),
    browserId: getBrowserId(),
  };
}

/**
 * Reset client identity completely.
 *
 * DANGER:
 * -------
 * Calling this during normal application use effectively changes the auth tuple:
 *
 *   (user_id, device_id, browser_id)
 *
 * That can break:
 * - session continuity
 * - refresh
 * - socket auth binding
 *
 * Therefore this must be considered a troubleshooting/dev-only helper.
 *
 * It should NOT be used by:
 * - login flow
 * - logout flow
 * - refresh flow
 * - normal UI navigation
 */
export function resetClientIdentity() {
  safeRemoveLS(LS_DEVICE_ID);
  safeRemoveLS(LS_BROWSER_ID);

  if (DEV) {
    // eslint-disable-next-line no-console
    console.log("[clientIdentity] identity reset.");
  }
}